import React, { useState } from 'react';
import {
  ArrowLeft,
  User,
  FileText,
  Download,
  Clock,
  AlertCircle,
  XCircle,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Shield,
  Briefcase,
  Eye,
  Heart,
  Loader2,
  IdCard,
  Info,
  FileUser,
  FileSpreadsheet,
  UserCheck,
  GraduationCap,
  Pencil,
} from 'lucide-react';
import { useGetApplicationByIdQuery, useUpdateApplicationMutation, useUpdateApplicationPermissionsMutation, useUpdateApplicationWorkInfoMutation } from '../store/services/applicationApi';
import StatusModal from '../components/StatusModal';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useMergedPdfDownload } from '../utils/useMergedPdfDownload';
import FileDisplay from '../components/FileDisplay';
import AdminFooter from '../components/AdminFooter';
import WorkInfoModal from '../components/WorkInfoModal';

const ApplicationDetails = () => {
  const { id } = useParams();
  const { data, isLoading, error, refetch } = useGetApplicationByIdQuery(id!);
  const [updateApplication] = useUpdateApplicationMutation();
  const [updateApplicationWorkInfo] = useUpdateApplicationWorkInfoMutation();
  const [updateApplicationPermissions] = useUpdateApplicationPermissionsMutation();
  const [activeTab, setActiveTab] = useState('first-phase');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [workInfoModalOpen, setWorkInfoModalOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);


  const { downloadAndViewMergedPdf } = useMergedPdfDownload();

  const navigate = useNavigate();

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'applied':
        return 'bg-blue-100 text-blue-800';
      case 'scheduled':
        return 'bg-purple-100 text-purple-800';
      case 'selected':
        return 'bg-green-100 text-green-800';
      case 'under-review':
        return 'bg-yellow-100 text-yellow-800';
      case 'submitted':
        return 'bg-indigo-100 text-indigo-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'applied':
        return <Clock className="w-4 h-4" />;
      case 'scheduled':
        return <Calendar className="w-4 h-4" />;
      case 'selected':
        return <UserCheck className="w-4 h-4" />;
      case 'under-review':
        return <AlertCircle className="w-4 h-4" />;
      case 'submitted':
        return <Clock className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to calculate age
  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Handle document download
  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  };

  const handleUpdateStatus = async (applicationId: string, updateData: { status: string; adminNotes?: string; rejectionReason?: string; remarkText?: string }) => {
    try {
      await updateApplication({
        id: applicationId,
        ...updateData,
      }).unwrap();

      toast.success(`Status updated to "${updateData.status.charAt(0).toUpperCase() + updateData.status.slice(1)}"`);
      await refetch();
    } catch (err: any) {
      toast.error(`Failed to update status: ${err?.data?.message || err.message}`);
    }
  };

  const handleSaveWorkInfo = async (payload: {
    employeeId: string;
    projectName: string;
    branch: string;
    shift: string;
    reference?: string;
  }) => {
    try {
      await updateApplicationWorkInfo({
        id: application._id,
        ...payload,
      }).unwrap();
      toast.success('Work information saved successfully');
      await refetch();
    } catch (err: any) {
      toast.error(`Failed to save work info: ${err?.data?.message || err.message}`);
      throw err;
    }
  };

  const handleTogglePermission = async (permissionKey: 'canEditFirstPhase' | 'canEditSecondPhase', value: boolean) => {
    try {
      await updateApplicationPermissions({
        id: application._id,
        [permissionKey]: value,
      }).unwrap();
      toast.success('Edit permission updated successfully');
      await refetch();
    } catch (err: any) {
      toast.error(`Failed to update permission: ${err?.data?.message || err.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading application details...</p>
        </div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Failed to load application details</p>
        </div>
      </div>
    );
  }

  const application = data.data.application ?? data.data;
  const profile = data.data.profile ?? data.data;
  const normalizeFileArray = (files: any, legacyFile?: any) => {
    if (Array.isArray(files)) {
      return files.filter((file) => file?.url);
    }

    if (legacyFile?.url) {
      return [legacyFile];
    }

    return [];
  };
  const normalizeIdentityDocs = (identity: any) => {
    const rawDocs = identity?.docFiles || [];

    if (!Array.isArray(rawDocs)) {
      return [];
    }

    return rawDocs
      .filter((doc) => doc?.url)
      .map((doc) => ({
        ...doc,
        type: doc?.type || '',
        side: doc?.side || '',
        name: doc?.name || doc?.fileName || 'Identity Document',
      }))
      .filter((doc) => ['nid', 'passport', 'birth-reg'].includes(String(doc.type).toLowerCase()))
      .slice(0, 4);
  };
  const getIdentityNumber = (identity: any) =>
    identity?.number || '';
  const getIdentityDocLabel = (doc: any) => {
    const normalizedType = String(doc?.type || '').toLowerCase();
    const normalizedSide = String(doc?.side || '').toLowerCase();
    const sideLabel = normalizedSide ? ` (${normalizedSide})` : '';

    if (normalizedType === 'nid') {
      return `NID${sideLabel}`;
    }

    if (normalizedType === 'passport') {
      return `Passport${sideLabel}`;
    }

    if (normalizedType === 'birth-reg' || normalizedType === 'birthreg' || normalizedType === 'birth_registration') {
      return `Birth Registration${sideLabel}`;
    }

    return doc?.name || 'Identity Document';
  };

  const cvFiles = Array.isArray(profile?.cvFiles) && profile.cvFiles.length > 0
    ? profile.cvFiles
    : profile?.cvFile?.url
      ? [profile.cvFile]
      : [];
  const testimonialFiles = normalizeFileArray(profile?.testimonialFiles, profile?.testimonialFile);
  const myVerifiedFiles = normalizeFileArray(profile?.myVerifiedFiles, profile?.myVerifiedFile);
  const commitmentFiles = normalizeFileArray(profile?.commitmentFiles, profile?.commitmentFile);
  const ndaFiles = Array.isArray(profile?.ndaFiles)
    ? profile.ndaFiles.filter((file: any) => file?.url)
    : [
        profile?.ndaFiles?.firstPageFile,
        profile?.ndaFiles?.secondPageFile,
      ].filter((file: any) => file?.url);
  const agreementFiles = Array.isArray(profile?.agreementFiles)
    ? profile.agreementFiles.filter((file: any) => file?.url)
    : [
        profile?.agreementFiles?.firstPageFile,
        profile?.agreementFiles?.secondPageFile,
      ].filter((file: any) => file?.url);
  const identityNumber = getIdentityNumber(profile?.identity);
  const identityDocs = normalizeIdentityDocs(profile?.identity);
  const editPermissions = profile?.editPermissions || {};

  return (
    <div className="min-h-screen relative flex flex-col">
      <div className="bg-overlay"></div>
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/20 bg-white/50 px-4 py-3 backdrop-blur-sm sm:px-6">

        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 sm:flex-row sm:items-center">

          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center space-x-2 text-center text-gray-600 transition-all duration-200 hover:scale-105 hover:text-gray-900 sm:justify-start sm:text-left"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold text-sm sm:text-base">Back to Previous Page</span>
          </button>

          {/* Tabs and Status */}
          <div className="grid w-full grid-cols-1 gap-3 min-[420px]:grid-cols-3 sm:w-auto sm:flex sm:flex-row sm:items-center sm:gap-4">

            {/* Status Button */}
            <button
              onClick={() => setStatusModalOpen(!statusModalOpen)}
              className={`flex w-full items-center justify-center space-x-2 rounded-lg border-2 px-4 py-2.5 shadow-sm sm:w-auto sm:justify-start ${getStatusColor(application.status)}`}
            >
              {getStatusIcon(application.status)}
              <span className="font-semibold capitalize text-sm">
                {application.status.replace('-', ' ')}
              </span>
            </button>

            {/* Tab Buttons */}
            {[
              { id: 'first-phase', label: '1st Phase', icon: FileText },
              { id: 'second-phase', label: '2nd Phase', icon: FileText },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex w-full items-center justify-center space-x-2 rounded-lg border px-4 py-2.5 text-sm font-medium text-slate-700 transition-all duration-200 sm:w-auto sm:justify-start whitespace-nowrap ${activeTab === tab.id
                  ? 'bg-white border-indigo-500 text-indigo-700 shadow-sm'
                  : 'border-slate-200 bg-white/70 hover:bg-white'
                  }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex-1 w-full max-w-7xl px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:gap-8">
          {/* Left Sidebar - Profile Card */}
          <div className="w-full flex-shrink-0 lg:w-80">
            <div className="overflow-hidden rounded-[12px] border border-white/20 bg-white/40 shadow-xl backdrop-blur-xs lg:sticky lg:top-20">
              {/* Profile Header */}
              <div className="relative bg-gradient-to-r from-red-400 to-red-600 p-6 text-center sm:p-8">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10">
                  <div className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-3xl border-4 border-white/30 bg-white/20 shadow-2xl backdrop-blur-sm sm:h-28 sm:w-28">
                    {profile?.basic?.profilePicFile?.url && (
                      <img
                        src={profile.basic.profilePicFile.url}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <h2 className="mb-2 break-words text-xl font-bold text-white sm:text-2xl">
                    {profile?.basic?.fullName || 'N/A'}
                  </h2>
                  <p className="text-blue-100 text-sm font-semibold mb-1">
                    {application?.applicationId || `#${application?._id?.slice(-8) || ''}`}
                  </p>
                  <p className="text-blue-200 text-sm">Job Applicant</p>
                </div>
              </div>

              {/* Basic Info */}
              <div className="space-y-4 p-5 sm:p-6">
                {profile?.basic?.email && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium">{profile.basic.email}</span>
                  </div>
                )}

                {profile?.basic?.phone && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-green-100">
                      <Phone className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium">{profile.basic.phone}</span>
                  </div>
                )}

                {profile?.address?.present && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-purple-100">
                      <MapPin className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium">
                      {[profile.address.present.street, profile.address.present.upazila, profile.address.present.district].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}

                {profile?.basic?.gender && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-orange-100">
                      <User className="w-4 h-4 text-orange-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium capitalize">{profile.basic.gender}</span>
                  </div>
                )}

                {profile?.basic?.dateOfBirth && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-100">
                      <Calendar className="w-4 h-4 text-red-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium">
                      {calculateAge(profile.basic.dateOfBirth)} years old
                    </span>
                  </div>
                )}

                {profile?.other?.maritalStatus && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-teal-100">
                      <Heart className="w-4 h-4 text-teal-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium capitalize">{profile.other.maritalStatus}</span>
                  </div>
                )}

                {(profile?.basic?.educationLevel ?? profile?.educationLevel ?? (profile as any)?.other?.educationLevel) && (
                  <div className="flex items-center space-x-3 text-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-100">
                      <GraduationCap className="w-4 h-4 text-indigo-600" />
                    </div>
                    <span className="min-w-0 break-words text-gray-900 font-medium">
                      {profile?.basic?.educationLevel ?? profile?.educationLevel ?? (profile as any)?.other?.educationLevel}
                    </span>
                  </div>
                )}
              </div>
              <div className="px-5 pb-5 sm:px-6 sm:pb-6">
                <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">First Phase Edit</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(editPermissions?.canEditFirstPhase)}
                      onClick={() => handleTogglePermission('canEditFirstPhase', !Boolean(editPermissions?.canEditFirstPhase))}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition ${
                        editPermissions?.canEditFirstPhase ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          editPermissions?.canEditFirstPhase ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Second Phase Edit</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={Boolean(editPermissions?.canEditSecondPhase)}
                      onClick={() => handleTogglePermission('canEditSecondPhase', !Boolean(editPermissions?.canEditSecondPhase))}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition ${
                        editPermissions?.canEditSecondPhase ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                          editPermissions?.canEditSecondPhase ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setPdfDownloading(true);
                  const files: { label: string; url: string }[] = [];
                  if (profile?.basic?.profilePicFile?.url) {
                    files.push({ label: "Profile Picture", url: profile.basic.profilePicFile.url });
                  }

                  if (identityDocs.length > 0) {
                    for (const doc of identityDocs) {
                      files.push({ label: getIdentityDocLabel(doc), url: doc.url });
                    }
                  }

                  if (cvFiles.length > 0) {
                    cvFiles.forEach((cv: any, index: number) => {
                      if (!cv?.url) {
                        return;
                      }

                      files.push({
                        label: cvFiles.length > 1 ? `Resume / CV ${index + 1}` : 'Resume / CV',
                        url: cv.url,
                      });
                    });
                  }

                  if (profile?.educationFiles?.sscCertFile) {
                    files.push({ label: 'SSC Certificate', url: profile?.educationFiles?.sscCertFile.url });
                  }

                  if (profile?.educationFiles?.lastCertFile) {
                    files.push({ label: 'Last Certificate', url: profile?.educationFiles?.lastCertFile.url });
                  }

                  testimonialFiles.forEach((file: any, index: number) => {
                    files.push({
                      label: testimonialFiles.length > 1
                        ? `Testimonial / Chairman Certificate ${index + 1}`
                        : 'Testimonial / Chairman Certificate',
                      url: file.url,
                    });
                  });

                  myVerifiedFiles.forEach((file: any, index: number) => {
                    files.push({
                      label: myVerifiedFiles.length > 1
                        ? `Personal Information Verified ${index + 1}`
                        : 'Personal Information Verified',
                      url: file.url,
                    });
                  });

                  commitmentFiles.forEach((file: any, index: number) => {
                    files.push({
                      label: commitmentFiles.length > 1
                        ? `Letter Of Understanding ${index + 1}`
                        : 'Letter Of Understanding',
                      url: file.url,
                    });
                  });

                  const ordinal = (n: number) => {
                    const s = ['th', 'st', 'nd', 'rd'];
                    const v = n % 100;
                    return n + (s[(v - 20) % 10] || s[v] || s[0]);
                  };

                  ndaFiles.forEach((file: any, index: number) => {
                    files.push({
                      label: `NDA Document - ${ordinal(index + 1)}`,
                      url: file.url,
                    });
                  });

                  agreementFiles.forEach((file: any, index: number) => {
                    files.push({
                      label: `Agreement Document - ${ordinal(index + 1)}`,
                      url: file.url,
                    });
                  });
                  console.log(files);

                  downloadAndViewMergedPdf(files, { application, profile })
                    .then(() => {
                      // Reset loading state after successful download
                      setTimeout(() => setPdfDownloading(false), 2000);
                    })
                    .catch((error) => {
                      console.error('PDF download failed:', error);
                      setPdfDownloading(false);
                    });
                }}
                disabled={pdfDownloading}
                className={`mx-5 mb-5 flex w-[calc(100%-2.5rem)] items-center justify-center gap-2 rounded px-4 py-2 text-sm transition-all duration-200 sm:mx-6 sm:mb-7 sm:w-[calc(100%-3rem)] ${
                  pdfDownloading 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {pdfDownloading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Downloading PDF</span>
                  </>
                ) : (
                  <>
                    Download Pdf File
                    <Download size={18} />
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="w-full min-w-0">
            {/* Content */}
            {activeTab === 'first-phase' && (
              <div className="w-full">
                {/* Identity */}
                <div className="details-section rounded-t-[12px]">
                  <div className="details-section-header">
                    <div className="details-section-icon bg-gradient-to-r from-indigo-500 to-blue-600">
                      <IdCard className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="details-section-title">Identity Information</h3>
                  </div>
                  <div className="details-section-body space-y-5">
                    {/* Identity Files */}
                    <div>
                      {identityNumber && (
                          <h4 className="details-field-label mb-3 inline-block">
                            Identity Number :{' '}
                            <span className="text-sm font-semibold text-slate-900 normal-case tracking-normal">
                              {identityNumber}
                            </span>
                          </h4>
                      )}

                      {identityDocs.length > 0 ? (
                        <div
                          className={`grid grid-cols-1 ${identityDocs.length > 1 ? 'lg:grid-cols-2' : ''
                            } gap-3 sm:gap-4`}
                        >
                          {identityDocs.map((doc: any, index: number) => {
                            const label = getIdentityDocLabel(doc);
                            return (
                              <div
                                key={index}
                                  className="details-doc-card bg-gradient-to-r from-green-50 to-emerald-50"
                                >
                                  <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0 pr-3">
                                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 shrink-0">
                                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="details-doc-title">{label}</p>
                                      <p className="details-doc-subtitle">{doc.name}</p>
                                    </div>
                                  </div>

                                <div className="shrink-0">
                                  <button
                                    onClick={() => window.open(doc.url, '_blank')}
                                      className="details-icon-button bg-blue-500"
                                    >
                                      <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                          <div className="details-empty">No identity documents found</div>
                        )}
                      </div>
                    </div>

                </div>
                {/* Emergency Contact */}
                  <div className="details-section">
                    <div className="details-section-header">
                      <div className="flex items-center space-x-3">
                        <div className="details-section-icon bg-gradient-to-r from-green-500 to-green-600">
                          <Phone className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="details-section-title">Emergency Contact</h3>
                      </div>
                    </div>
                    <div className="details-section-body">
                      {profile?.emergencyContact?.name || profile?.emergencyContact?.phone ? (
                        <div className="details-field-grid">
                          {profile.emergencyContact.name && (
                            <div>
                              <label className="details-field-label">Full Name:</label>
                              <p className="details-field-value">{profile.emergencyContact.name}</p>
                            </div>
                          )}
                          {profile.emergencyContact.phone && (
                            <div>
                              <label className="details-field-label">Phone:</label>
                              <p className="details-field-value">{profile.emergencyContact.phone}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="details-empty">No emergency contact found</div>
                      )}
                    </div>
                  </div>
                  {/* Address Info */}
                  <div className="details-section">
                    <div className="details-section-header">
                      <div className="flex items-center space-x-3">
                        <div className="details-section-icon bg-gradient-to-r from-green-500 to-emerald-600">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="details-section-title">Address Information</h3>
                      </div>
                    </div>
                    <div className="details-section-body">
                      {(profile?.address?.present && (profile.address.present.street || profile.address.present.upazila || profile.address.present.district)) ||
                        (profile?.address?.permanent && (profile.address.permanent.street || profile.address.permanent.upazila || profile.address.permanent.district)) ? (
                        <div className="details-field-grid">
                        {/* Present Address */}
                        {profile?.address?.present && (profile.address.present.street || profile.address.present.upazila || profile.address.present.district) && (
                            <div>
                              <label className="details-field-label">Present Address:</label>
                              <p className="details-field-value">
                                {[profile.address.present.street, profile.address.present.upazila, profile.address.present.district].filter(Boolean).join(', ')}
                              </p>
                            </div>
                        )}
                        {/* Permanent Address */}
                        {profile?.address?.permanent && (profile.address.permanent.street || profile.address.permanent.upazila || profile.address.permanent.district) && (
                            <div>
                              <label className="details-field-label">Permanent Address:</label>
                              <p className="details-field-value">
                                {[profile.address.permanent.street, profile.address.permanent.upazila, profile.address.permanent.district].filter(Boolean).join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="details-empty">No address information found</div>
                      )}
                    </div>
                  </div>
                {/* Other Information */}
                  <div className="details-section">
                    <div className="details-section-header">
                      <div className="flex items-center space-x-3">
                        <div className="details-section-icon bg-gradient-to-r from-yellow-500 to-orange-600">
                          <Info className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="details-section-title">Other Information</h3>
                      </div>
                    </div>
                    <div className="details-section-body">
                      {profile?.other && (profile.other.fathersName || profile.other.mothersName) ? (
                        <div className="details-field-grid">
                          {profile.other.fathersName && (
                            <div>
                              <label className="details-field-label">Father's Name:</label>
                              <p className="details-field-value">{profile.other.fathersName}</p>
                            </div>
                          )}
                          {profile.other.mothersName && (
                            <div>
                              <label className="details-field-label">Mother's Name:</label>
                              <p className="details-field-value">{profile.other.mothersName}</p>
                            </div>
                          )}
                          {profile.other.religion && (
                            <div>
                              <label className="details-field-label">Religion:</label>
                              <p className="details-field-value">{profile.other.religion}</p>
                            </div>
                          )}
                          {profile.other.gender && (
                            <div>
                              <label className="details-field-label">Gender:</label>
                              <p className="details-field-value capitalize">{profile.other.gender}</p>
                            </div>
                          )}
                          {profile.other.maritalStatus && (
                            <div>
                              <label className="details-field-label">Marital Status:</label>
                              <p className="details-field-value capitalize">{profile.other.maritalStatus}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="details-empty">No other information found</div>
                      )}
                    </div>
                  </div>
                {/* CV Document */}
                <div className="details-section">
                  <div className="details-section-header">
                    <div className="details-section-icon bg-gradient-to-r from-indigo-500 to-blue-600">
                      <IdCard className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="details-section-title">CV Document</h3>
                  </div>
                  <div className="details-section-body space-y-4">
                    {cvFiles.length > 0 ? (
                      cvFiles.map((cv: any, index: number) => (
                        <div
                          key={cv._id || cv.url || index}
                          className="details-doc-card bg-gradient-to-r from-blue-50 to-indigo-50"
                        >
                          <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0 pr-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 shrink-0">
                              <FileUser className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="details-doc-title">
                                {cv?.name || `CV File ${index + 1}`}
                              </p>
                              <p className="details-doc-subtitle">
                                {cvFiles.length > 1 ? `CV Document ${index + 1}` : 'CV Document'}
                              </p>
                            </div>
                          </div>

                          <div className="flex space-x-2 sm:space-x-3 shrink-0">
                            <button
                              onClick={() => window.open(cv.url, '_blank')}
                              className="details-icon-button bg-blue-500"
                            >
                              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                            <button
                              onClick={() => handleDownload(cv.url, cv?.name || `cv-file-${index + 1}`)}
                              className="details-icon-button bg-green-500"
                            >
                              <Download className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="details-empty">No CV document found</div>
                    )}
                  </div>
                </div>
                {/* Application Info */}
                <div className="details-section rounded-b-[12px]">
                  <div className="details-section-header">
                    <div className="flex items-center space-x-3">
                      <div className="details-section-icon bg-gradient-to-r from-green-500 to-teal-600">
                        <Briefcase className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="details-section-title">Application Details</h3>
                    </div>
                  </div>
                  <div className="details-section-body">
                    {application && (application._id || application.status || application.submittedAt || application.createdAt) ? (
                      <>
                        <div className="details-field-grid-3">
                          {application.status && (
                            <div>
                              <label className="details-field-label">Status:</label>
                              <div className={`inline-flex items-center gap-2 align-middle`}>
                                {getStatusIcon(application.status)}
                                <span className="inline-flex items-center text-sm font-semibold capitalize leading-6 text-slate-900">
                                  {application.status.replace('-', ' ')}
                                </span>
                              </div>
                            </div>
                          )}
                          {application.submittedAt && (
                            <div>
                              <label className="details-field-label">Submitted At:</label>
                              <p className="details-field-value">{formatDate(application.submittedAt)}</p>
                            </div>
                          )}
                          {application.createdAt && (
                            <div>
                              <label className="details-field-label">Created At:</label>
                              <p className="details-field-value">{formatDate(application.createdAt)}</p>
                            </div>
                          )}
                        </div>
                        {application.rejectionReason && (
                          <div>
                            <label className="details-field-label">Rejection Reason:</label>
                            <p className="details-field-value font-mono">{application.rejectionReason}</p>
                          </div>
                        )}
                        {application.remarkText && (
                          <div>
                            <label className="details-field-label">Remark Text:</label>
                            <p className="details-field-value font-mono">{application.remarkText}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="details-empty">No application details found</div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'second-phase' && (
              <div className="w-full">
                {/* Work Info */}
                <div className="details-section rounded-t-[12px]">
                  <div className="details-section-header">
                    <div className="flex items-center justify-between gap-3 w-full">
                      <div className="flex items-center space-x-3">
                        <div className="details-section-icon bg-gradient-to-r from-yellow-500 to-amber-600">
                          <Briefcase className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="details-section-title">Work Information</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setWorkInfoModalOpen(true)}
                        className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        <Pencil className="h-4 w-4" />
                        <span>{profile?.workInfo ? 'Edit' : 'Add'}</span>
                      </button>
                    </div>
                  </div>
                  <div className="details-section-body">
                    {profile?.workInfo && (profile.workInfo.employeeId || profile.workInfo.projectName || profile.workInfo.branch || profile.workInfo.shift || profile.workInfo.reference) ? (
                      <div className="details-field-grid-3">
                        {profile.workInfo.employeeId && (
                          <div>
                            <label className="details-field-label">Employee ID:</label>
                            <p className="details-field-value">{profile.workInfo.employeeId}</p>
                          </div>
                        )}
                        {profile.workInfo.projectName && (
                          <div>
                            <label className="details-field-label">Project:</label>
                            <p className="details-field-value">{profile.workInfo.projectName}</p>
                          </div>
                        )}
                        {profile.workInfo.branch && (
                          <div>
                            <label className="details-field-label">Branch:</label>
                            <p className="details-field-value">{profile.workInfo.branch}</p>
                          </div>
                        )}
                        {profile.workInfo.shift && (
                          <div>
                            <label className="details-field-label">Shift:</label>
                            <p className="details-field-value capitalize">{profile.workInfo.shift}</p>
                          </div>
                        )}
                        {profile.workInfo.reference && (
                          <div>
                            <label className="details-field-label">Reference:</label>
                            <p className="details-field-value">{profile.workInfo.reference}</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="details-empty">No work information found</div>
                    )}
                  </div>
                </div>

                {/* Education Documents */}
                <div className="details-section">
                  <div className="details-section-header">
                    <div className="flex items-center space-x-3">
                      <div className="details-section-icon bg-gradient-to-r from-green-500 to-emerald-600">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="details-section-title">Education Documents</h3>
                    </div>
                  </div>
                  <div className="details-section-body space-y-6">
                    {/* SSC Certificate */}
                    {profile?.educationFiles?.sscCertFile &&
                      <FileDisplay
                        title="SSC Certificate / Mark Sheet"
                        file={profile.educationFiles.sscCertFile}
                        gradient="from-green-100 to-emerald-100"
                        iconColor="text-emerald-600"
                      />
                    }

                    {/* Last Certificate */}
                    {profile?.educationFiles?.lastCertFile &&
                      <FileDisplay
                        title="Last Certificate / Mark Sheet"
                        file={profile.educationFiles.lastCertFile}
                        gradient="from-green-100 to-emerald-100"
                        iconColor="text-emerald-600"
                      />
                    }

                    {!profile?.educationFiles?.sscCertFile &&
                      !profile?.educationFiles?.lastCertFile && (
                          <div className="details-empty">No education documents found</div>
                        )}
                  </div>
                </div>

                {/* Personal Info */}
                <div className="details-section">
                  <div className="details-section-header">
                    <div className="flex items-center space-x-3">
                      <div className="details-section-icon bg-gradient-to-r from-blue-500 to-indigo-600">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="details-section-title">Personal Documents</h3>
                    </div>
                  </div>
                  <div className="details-section-body space-y-6">
                    {/* Testimonial */}
                    {testimonialFiles.length > 0 ? (
                      testimonialFiles.map((file: any, index: number) => (
                        <FileDisplay
                          key={file?.url || index}
                          title={testimonialFiles.length > 1 ? `Testimonial / Chairman Certificate ${index + 1}` : 'Testimonial / Chairman Certificate'}
                          file={file}
                          gradient="from-blue-100 to-indigo-100"
                          iconColor="text-indigo-600"
                        />
                      ))
                    ) : (
                      <div className="details-empty">No personal documents found</div>
                    )}
                  </div>
                </div>

                {/* Official Info */}
                <div className="details-section rounded-b-[12px]">
                  <div className="details-section-header">
                    <div className="flex items-center space-x-3">
                      <div className="details-section-icon bg-gradient-to-r from-purple-500 to-pink-600">
                        <FileSpreadsheet className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="details-section-title">Official Documents</h3>
                    </div>
                  </div>
                  <div className="details-section-body space-y-6">
                    {/* My Verified File */}
                    {myVerifiedFiles.map((file: any, index: number) => (
                      <FileDisplay
                        key={file?.url || `verified-${index}`}
                        title={myVerifiedFiles.length > 1 ? `Personal Info Verified ${index + 1}` : 'Personal Info Verified'}
                        file={file}
                        gradient="from-blue-100 to-indigo-100"
                        iconColor="text-indigo-600"
                      />
                    ))}

                    {commitmentFiles.map((file: any, index: number) => (
                      <FileDisplay
                        key={file?.url || `commitment-${index}`}
                        title={commitmentFiles.length > 1 ? `Letter of Understanding ${index + 1}` : 'Letter of Understanding'}
                        file={file}
                        gradient="from-blue-100 to-indigo-100"
                        iconColor="text-indigo-600"
                      />
                    ))}

                    {ndaFiles.map((file: any, index: number) => (
                      <FileDisplay
                        key={file?.url || `nda-${index}`}
                        title={file?.name || `NDA - Page ${index + 1}`}
                        file={file}
                        gradient="from-blue-100 to-indigo-100"
                        iconColor="text-indigo-600"
                      />
                    ))}

                    {agreementFiles.map((file: any, index: number) => (
                      <FileDisplay
                        key={file?.url || `agreement-${index}`}
                        title={file?.name || `Agreement - Page ${index + 1}`}
                        file={file}
                        gradient="from-blue-100 to-indigo-100"
                        iconColor="text-indigo-600"
                      />
                    ))}

                    {!myVerifiedFiles.length &&
                      !commitmentFiles.length &&
                      !agreementFiles.length &&
                      !ndaFiles.length && (
                        <div className="details-empty">No official documents found</div>
                      )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Status Modal */}
      <StatusModal
        isOpen={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        application={application}
        onUpdateStatus={handleUpdateStatus}
      />
      <WorkInfoModal
        isOpen={workInfoModalOpen}
        onClose={() => setWorkInfoModalOpen(false)}
        initialWorkInfo={profile?.workInfo}
        onSave={handleSaveWorkInfo}
      />
      <AdminFooter />

    </div>
  );
};

export default ApplicationDetails;
